/*
 * VPDB - Virtual Pinball Database
 * Copyright (C) 2019 freezy <freezy@vpdb.io>
 *
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; either version 2
 * of the License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 * Modified for sigmashake-pinball by SigmaShake, 2026.
 */

import type { ItemFrame } from "../../../shared/contracts";
import { EventProxy } from "../../core/event-proxy";
import type { IHittable } from "../../core/ihittable";
import type { IMovable } from "../../core/imovable";
import { Item } from "../../core/item";
import type { Player } from "../../core/player";
import type { HitObject } from "../../physics/hit-object";
import type { Table } from "../../table-model";
import type { PlungerData } from "./plunger-data";
import { PlungerHit } from "./plunger-hit";
import type { PlungerMover } from "./plunger-mover";
import { PlungerState } from "./plunger-state";

/**
 * The plunger. It launches the ball into the playfield.
 */
export class Plunger extends Item<PlungerData> implements IMovable, IHittable {
  public static PLUNGER_HEIGHT = 50.0;

  private readonly state: PlungerState;
  private hit?: PlungerHit;

  public constructor(data: PlungerData) {
    super(data);
    this.state = new PlungerState(this.getName(), data.isVisible);
  }

  public getState(): PlungerState {
    return this.state;
  }

  public isCollidable(): boolean {
    return true;
  }

  public setupPlayer(player: Player, table: Table): void {
    this.events = new EventProxy(this, "plunger", player.getEventSink());
    this.hit = new PlungerHit(this.data, this.state, this.events, table);
  }

  public getMover(): PlungerMover {
    return this.hit!.getMoverObject();
  }

  public getHitShapes(): HitObject[] {
    return [this.hit!];
  }

  /** Button down: pull the plunger back. */
  public pullBack(): void {
    this.getMover().pullBack(this.data.speedPull);
  }

  /** Button up: fire the plunger from the current position. An auto plunger fires at full strength. */
  public fire(): void {
    if (this.data.autoPlunger) {
      this.getMover().fire(1.0);
    } else {
      this.getMover().fire();
    }
  }

  /** Touch input: hold the plunger at a pull position from 0 to 1. */
  public holdAt(relPos: number): void {
    this.getMover().holdAt(relPos);
  }

  public toFrame(): ItemFrame {
    return this.state.toFrame();
  }
}

export interface PlungerConfig {
  x: number;
  y: number;
  x2: number;
  zHeight: number;
  frameTop: number;
  frameBottom: number;
}
